/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B050020 仕入照会
 */

import { Column, Query } from "@material-table/core";
import {
  B000170ShireNmr,
  B050020Delete,
  B050020DeleteRequest,
  B050020Find,
  B050020FindRequest,
  B050020Get,
  catchRestError,
} from "clients/rest";
import {
  msgState,
  SCancelButton,
  SCodeSelect,
  SConfirmDialog,
  SDateField,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  SText,
  STextField,
} from "components";
import { getClientError, getClientMessage } from "messages";
import { B050020SyuturyokuKbn, getSCode, SCode } from "messages/codes";
import { labels } from "messages/labels";
import { B000130, B000140, B000170, B050010InputData } from "pages";
import { B000120 } from "pages/b000/B000120";
import { createRef, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { date, dateWithSlashAt, mapIds } from "services";
import { editKbn, eigyoKengen } from "services/codes";
import {
  authState,
  focusState,
  menusState,
  navigateTypeState,
  useFocus,
} from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// 検索条件を格納するデータタイプ
type SearchedFields = {
  searched: boolean;
} & Partial<{
  fields: Fields;
  page: number;
  pageSize: number;
  sort: string[];
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  shireYmdFrom: string;
  shireYmdTo: string;
  eigyoCdFrom: string;
  eigyoCdTo: string;
  bumonCdFrom: string;
  bumonCdTo: string;
  shainCdFrom: string;
  shainCdTo: string;
  hachudenpyoNoFrom: string;
  hachudenpyoNoTo: string;
  shireCd: string;
  shireNmr: string;
  syuturyokuKbn: SCode;
}>;

// テーブル定義
type Data = Partial<{
  gyoNo: string;
  shiredenpyoNo: string;
  shireYmdF: string;
  hachudenpyoNoF: string;
  shireNm: string;
  shainNm: string;
  bumonNm: string;
  shireKin: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  shireYmdFrom: "",
  shireYmdTo: "",
  eigyoCdFrom: "",
  eigyoCdTo: "",
  bumonCdFrom: "",
  bumonCdTo: "",
  shainCdFrom: "",
  shainCdTo: "",
  hachudenpyoNoFrom: "",
  hachudenpyoNoTo: "",
  shireCd: "",
  shireNmr: "",
  syuturyokuKbn: getSCode(B050020SyuturyokuKbn, "0"),
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "4rem", align: "right" },
  {
    field: "shireYmdF",
    title: "仕入日",
    width: "6rem",
    render: (data) => {
      if (data.shireYmdF! !== null && data.shireYmdF! !== "") {
        return dateWithSlashAt(data.shireYmdF!);
      }
    },
  },
  { field: "hachudenpyoNoF", title: "発注No.", width: "8rem" },
  { field: "shireNm", title: "仕入先", width: "15rem" },
  { field: "shainNm", title: "仕入担当者", width: "8rem" },
  { field: "bumonNm", title: "仕入担当部門", width: "6rem" },
  {
    field: "shireKin",
    title: "仕入金額　",
    width: "6rem",
    align: "right",
    render: (data) => data.shireKin?.toLocaleString(),
  },
  { field: "shiredenpyoNo", title: "仕入伝票番号", hidden: true },
];

const focusId = "B050020";

// B050020 仕入照会
export const B050020 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

// 検索条件を格納するステート
const B050020State = atom<SearchedFields>({
  key: "B050020State",
  default: {
    searched: false,
    fields: defaultFields,
  },
});

const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするステート
  const isDefaultPage = useRef(false);
  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // テーブル行で選択されたデータ
  const [rowData, setRowData] = useState<Partial<Data>>();
  // 検索条件保存
  const [searchedFeields, setSearchedFields] = useRecoilState(B050020State);

  // メッセージ表示
  const setMsg = useSetRecoilState(msgState);

  // 画面遷移の処理
  const navigate = useNavigate();
  const [menuNavigate, setMenuNavigate] = useRecoilState(navigateTypeState);

  // ボタンの活性・非活性
  const [disabledShireDetail, setDisabledShireDetail] = useState(true);
  const [disabledShireDelete, setDisabledShireDelete] = useState(true);

  // ログインユーザー情報
  const auth = useRecoilValue(authState);
  // 所属営業所コードでの営業所コード入力制御
  const [isEigyoCdHonsha, setIsEigyoCdHonsha] = useState(true);
  // menusステート取得
  const menus = useRecoilValue(menusState);
  // 営業所権限取得
  const eigyosyoKengen = menus["050"].pages["050020"].eigyosyoKengen;
  const pageB050010 = menus["050"].pages["050010"];
  const pageB050011 = menus["050"].pages["050011"];

  // 初期表示イベント
  useEffect(() => {
    if (menuNavigate === "menu") {
      // 自営業所の設定
      // イベント利用権限定義マスタ.営業所権限が"0"(所属営業所のみ指定可)の場合、
      if (eigyosyoKengen === eigyoKengen.only) {
        // 自営業所コード(変更不可)の設定
        methods.setValue("eigyoCdFrom", auth?.eigyoCd!);
        methods.setValue("eigyoCdTo", auth?.eigyoCd!);
        // 入力部非活性化
        setIsEigyoCdHonsha(false);
      }
      // 利用権限定義マスタ.営業所権限が"1"(全営業所指定可権限)の場合、
      else if (eigyosyoKengen === eigyoKengen.all) {
        // 空白(入力可能)
        setIsEigyoCdHonsha(true);
      }

      // 仕入日初期表示
      (async () => {
        await methods.setValue("shireYmdFrom", date());
        methods.setValue("shireYmdTo", date());
        focus(1);
      })();
      return;
    }

    if (searchedFeields.searched) {
      // 検索条件が保存されている
      const fields = searchedFeields.fields;
      if (typeof fields !== "undefined") {
        methods.setValue("shireYmdFrom", fields.shireYmdFrom);
        methods.setValue("shireYmdTo", fields.shireYmdTo);
        methods.setValue("eigyoCdFrom", fields.eigyoCdFrom);
        methods.setValue("eigyoCdTo", fields.eigyoCdTo);
        methods.setValue("bumonCdFrom", fields.bumonCdFrom);
        methods.setValue("bumonCdTo", fields.bumonCdTo);
        methods.setValue("shainCdFrom", fields.shainCdFrom);
        methods.setValue("shainCdTo", fields.shainCdTo);
        methods.setValue("hachudenpyoNoFrom", fields.hachudenpyoNoFrom);
        methods.setValue("hachudenpyoNoTo", fields.hachudenpyoNoTo);
        methods.setValue("shireCd", fields.shireCd);
        methods.setValue("shireNmr", fields.shireNmr);
        methods.setValue("syuturyokuKbn", fields.syuturyokuKbn);
        setSearched(true);
        return;
      }
    }
  }, []);

  // テーブル行選択時
  const handleSelectTableRow = (_: any, rowData: Data | undefined) => {
    // 行が選択されていないときはボタン非活性
    const disabled = typeof rowData === "undefined";
    setDisabledShireDetail(disabled);
    setDisabledShireDelete(disabled);

    // データを保存
    setRowData(rowData);
  };

  // テーブル行ダブルクリック時
  const handleDoubleClickTableRow = (data: Data) => {
    // 仕入データの検索を行う
    B050020Get({ shiredenpyoNo: data.shiredenpyoNo })
      .then((value) => {
        // 月次更新日・発注伝票番号・受注伝票番号 いずれかが設定されている場合
        // ⇒参照モード
        const inputData: B050010InputData = {
          editKbn: editKbn.reference,
          shire: value.data,
        };
        // 月次更新日・発注伝票番号・受注伝票番号 いずれも設定されていない場合
        // ⇒修正モード
        if (
          !value.data[0].getsujiYmd &&
          !value.data[0].hachudenpyoNo &&
          !value.data[0].juchudenpyoNo
        ) {
          inputData.editKbn = editKbn.update;
        }

        setSearchedFields({ searched: true, fields: methods.getValues() });
        setMenuNavigate("next");

        // 仕入入力(本社)画面へ遷移する。
        if (pageB050010) {
          navigate("/b050010", { state: inputData });
          // 仕入入力(物流)画面へ遷移する。
        } else if (pageB050011) {
          navigate("/b050011", { state: inputData });
          // 上記のどちらでもない人
        } else {
          setMsg(getClientError("B050020_001"));
        }
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 詳細ボタンクリック時
  const handleClickShowDetailButton = () => {
    // 行が選択されている場合のみ実行する。
    if (typeof rowData === "undefined") {
      return;
    }
    // 仕入データの検索を行う
    B050020Get({ shiredenpyoNo: rowData.shiredenpyoNo })
      .then((value) => {
        // 月次更新日・発注伝票番号・受注伝票番号 いずれかが設定されている場合
        // ⇒参照モード
        const inputData: B050010InputData = {
          editKbn: editKbn.reference,
          shire: value.data,
        };
        // 月次更新日・発注伝票番号・受注伝票番号 いずれも設定されていない場合
        // ⇒修正モード
        if (
          !value.data[0].getsujiYmd &&
          !value.data[0].hachudenpyoNo &&
          !value.data[0].juchudenpyoNo
        ) {
          inputData.editKbn = editKbn.update;
        }

        setSearchedFields({ searched: true, fields: methods.getValues() });
        setMenuNavigate("next");

        // 仕入入力(本社)画面へ遷移する。
        if (pageB050010) {
          navigate("/b050010", { state: inputData });
          // 仕入入力(物流)画面へ遷移する。
        } else if (pageB050011) {
          navigate("/b050011", { state: inputData });
          // 上記のどちらでもない人
        } else {
          setMsg(getClientError("B050020_001"));
        }
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  // 検索時の初期化処理
  const handleResetSearch = () => {
    // 各ボタン非活性化
    setDisabledShireDetail(true);
    setDisabledShireDelete(true);
    // 選択されているデータを空にする
    setRowData({});
    (tableRef.current as any).handleClearSelect();
  };

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 初期化
    handleResetSearch();
    // 削除ボタン非活性化
    setDisabledShireDelete(true);
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }

      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 出力順に応じたソート処理
      let order;
      if (fields.syuturyokuKbn?.value === "0") {
        order = ["shireYmd,asc", "shiredenpyoNo,asc"];
      } else if (fields.syuturyokuKbn?.value === "1") {
        order = ["shireCd,asc", "shiredenpyoNo,asc"];
      }

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B050020FindRequest = {
        shireYmdFrom: fields.shireYmdFrom,
        shireYmdTo: fields.shireYmdTo,
        eigyoCdFrom: fields.eigyoCdFrom,
        eigyoCdTo: fields.eigyoCdTo,
        bumonCdFrom: fields.bumonCdFrom,
        bumonCdTo: fields.bumonCdTo,
        shainCdFrom: fields.shainCdFrom,
        shainCdTo: fields.shainCdTo,
        hachudenpyoNoFrom: fields.hachudenpyoNoFrom,
        hachudenpyoNoTo: fields.hachudenpyoNoTo,
        shireCd: fields.shireCd,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: order,
      };
      // API呼出し
      B050020Find(param)
        // 正常値取得
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // 仕入削除イベント
  const handleClickDeleteButton = () => {
    // 行が選択されている場合のみ実行する。
    if (typeof rowData === "undefined") {
      return;
    }
    // リクエスト作成
    const param: B050020DeleteRequest = {
      shiredenpyoNo: rowData.shiredenpyoNo,
    };
    // API呼び出し
    B050020Delete(param)
      .then(() => {
        // 削除後に先頭ページに戻る
        isDefaultPage.current = true;
        // テーブル再検索
        (tableRef.current as any).onQueryChange();
        // 各ボタン非活性化
        setDisabledShireDetail(true);
        setDisabledShireDelete(true);
        // 削除完了メッセージ設定
        setMsg(getClientMessage("B050020_001"));
      })
      .catch((reason) => {
        catchRestError(reason, navigate, setMsg);
      });
  };

  //   レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="仕入照会"
        gridTemplateColumns="6rem 8rem 1rem 8rem 4rem 3rem 6rem 7rem 1rem 7rem"
      >
        <SLabel>仕入日</SLabel>
        <SDateField name="shireYmdFrom" {...register(1)} />
        <SText>～</SText>
        <SDateField name="shireYmdTo" {...register(2)} />

        <SLabel gridColumn="7">営業所</SLabel>
        {isEigyoCdHonsha ? (
          <SNumberField
            code
            label="営業所コード（開始）"
            name="eigyoCdFrom"
            {...register(3)}
            size={6}
            dialog={<B000120 />}
            handleSubmit={(data: any) => {
              methods.setValue("eigyoCdFrom", data.eigyoCd);
            }}
          />
        ) : (
          <SNumberField
            code
            label="営業所コード（開始）"
            name="eigyoCdFrom"
            disabled
          />
        )}
        <SText>～</SText>
        {isEigyoCdHonsha ? (
          <SNumberField
            code
            label="営業所コード（終了）"
            name="eigyoCdTo"
            {...register(4)}
            size={6}
            dialog={<B000120 />}
            handleSubmit={(data: any) => {
              methods.setValue("eigyoCdTo", data.eigyoCd);
            }}
          />
        ) : (
          <SNumberField
            code
            label="営業所コード（終了）"
            name="eigyoCdTo"
            disabled
          />
        )}

        <SLabel gridColumn="1">仕入担当部門</SLabel>
        <SNumberField
          name="bumonCdFrom"
          code
          size={6}
          dialog={<B000130 />}
          handleSubmit={(data: any) => {
            methods.setValue("bumonCdFrom", data.bumonCd);
          }}
          {...register(5)}
        />
        <SText>～</SText>
        <SNumberField
          name="bumonCdTo"
          code
          size={6}
          dialog={<B000130 />}
          handleSubmit={(data: any) => {
            methods.setValue("bumonCdTo", data.bumonCd);
          }}
          {...register(6)}
        />
        <SLabel gridColumn="7">仕入担当者</SLabel>
        <SNumberField
          name="shainCdFrom"
          code
          size={7}
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("shainCdFrom", data.shainCd);
          }}
          {...register(7)}
        />
        <SText>～</SText>
        <SNumberField
          name="shainCdTo"
          code
          size={7}
          dialog={<B000140 />}
          handleSubmit={(data: any) => {
            methods.setValue("shainCdTo", data.shainCd);
          }}
          {...register(8)}
        />

        <SLabel gridColumn="1">発注No.</SLabel>
        <SNumberField
          name="hachudenpyoNoFrom"
          code
          size={10}
          {...register(9)}
        />
        <SText>～</SText>
        <SNumberField name="hachudenpyoNoTo" code size={10} {...register(10)} />

        <SLabel gridColumn="1">仕入先</SLabel>
        <SNumberField
          name="shireCd"
          code
          size={6}
          onBlur={() => B000170ShireNmr(methods, navigate, setMsg)}
          dialog={<B000170 />}
          handleSubmit={(data: any) => {
            methods.setValue("shireCd", data.shireCd);
            methods.setValue("shireNmr", data.shireNmr);
          }}
          {...register(11)}
        />
        <STextField name="shireNmr" size={24} disabled gridColumn="span 3" />

        <SLabel gridColumn="1">出力順</SLabel>
        <SCodeSelect
          label="出力順"
          name="syuturyokuKbn"
          options={B050020SyuturyokuKbn}
          {...register(12)}
        />

        <SNormalButton
          label={labels.buttons.search}
          gridRow="9"
          gridColumn="10"
          align="right"
          onClick={handleClickSearchButton}
          {...register(13)}
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          width={1260}
          height={300}
          onRowClick={handleSelectTableRow}
          handleDoubleClick={handleDoubleClickTableRow}
          onPageChange={handleResetSearch}
        />
      </SFrame>
      <SFrame gridTemplateColumns="73.7rem 5rem">
        <SCancelButton
          label={labels.buttons.delete}
          gridColumn={1}
          {...register(15)}
          disabled={disabledShireDelete}
          dialog={
            <SConfirmDialog
              title="仕入データ削除"
              msg="選択中の仕入データを削除します。よろしいですか？"
            />
          }
          dialogAction="confirm"
          handleSubmit={handleClickDeleteButton}
        />
        <SNormalButton
          label={labels.buttons.detail}
          disabled={disabledShireDetail}
          onClick={handleClickShowDetailButton}
          align="right"
          gridColumn={2}
          {...register(14)}
        />
      </SFrame>
    </FormProvider>
  );
};
